
import React, { useState } from 'react';
import { Clock } from 'lucide-react';

interface OpeningTimesSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

const OpeningTimesSelector: React.FC<OpeningTimesSelectorProps> = ({ value, onChange }) => {
  const [schedule, setSchedule] = useState(() => {
    // Parse existing value if any
    if (value) {
      try {
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        const scheduleObj: Record<string, { open: string; close: string; closed: boolean }> = {};
        
        // Initialize with closed days
        days.forEach(day => {
          scheduleObj[day] = { open: '09:00', close: '17:00', closed: true };
        });

        // Parse the existing format
        const parts = value.split(', ');
        parts.forEach(part => {
          const [daysPart, timesPart] = part.split(' ');
          if (timesPart) {
            const [open, close] = timesPart.split('-');
            if (daysPart.includes('-')) {
              // Range like "Mon-Fri"
              const [start, end] = daysPart.split('-');
              const dayMap: Record<string, number> = {
                'Mon': 0, 'Tue': 1, 'Wed': 2, 'Thu': 3, 'Fri': 4, 'Sat': 5, 'Sun': 6
              };
              const startIdx = dayMap[start];
              const endIdx = dayMap[end];
              for (let i = startIdx; i <= endIdx; i++) {
                scheduleObj[days[i]] = { open, close, closed: false };
              }
            } else {
              // Single day or specific days
              const dayMap: Record<string, string> = {
                'Mon': 'Monday', 'Tue': 'Tuesday', 'Wed': 'Wednesday', 
                'Thu': 'Thursday', 'Fri': 'Friday', 'Sat': 'Saturday', 'Sun': 'Sunday'
              };
              if (dayMap[daysPart]) {
                scheduleObj[dayMap[daysPart]] = { open, close, closed: false };
              }
            }
          }
        });

        return scheduleObj;
      } catch (e) {
        // Fallback to default
      }
    }
    
    return {
      Monday: { open: '09:00', close: '17:00', closed: false },
      Tuesday: { open: '09:00', close: '17:00', closed: false },
      Wednesday: { open: '09:00', close: '17:00', closed: false },
      Thursday: { open: '09:00', close: '17:00', closed: false },
      Friday: { open: '09:00', close: '17:00', closed: false },
      Saturday: { open: '09:00', close: '17:00', closed: true },
      Sunday: { open: '09:00', close: '17:00', closed: true }
    };
  });

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const updateSchedule = (day: string, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    const newSchedule = {
      ...schedule,
      [day]: {
        ...schedule[day],
        [field]: value
      }
    };
    setSchedule(newSchedule);
    
    // Convert to string format
    const formatted = formatSchedule(newSchedule);
    onChange(formatted);
  };

  const formatSchedule = (scheduleObj: typeof schedule) => {
    const openDays = days.filter(day => !scheduleObj[day].closed);
    
    if (openDays.length === 0) return 'Closed';
    
    // Group consecutive days with same hours
    const groups: { days: string[]; hours: string }[] = [];
    
    openDays.forEach(day => {
      const hours = `${scheduleObj[day].open}-${scheduleObj[day].close}`;
      const existingGroup = groups.find(g => g.hours === hours);
      
      if (existingGroup) {
        existingGroup.days.push(day);
      } else {
        groups.push({ days: [day], hours });
      }
    });

    return groups.map(group => {
      const dayAbbr = (day: string) => day.substring(0, 3);
      
      if (group.days.length === 1) {
        return `${dayAbbr(group.days[0])} ${group.hours}`;
      } else if (group.days.length === 5 && 
                 group.days.includes('Monday') && 
                 group.days.includes('Tuesday') && 
                 group.days.includes('Wednesday') && 
                 group.days.includes('Thursday') && 
                 group.days.includes('Friday')) {
        return `Mon-Fri ${group.hours}`;
      } else if (group.days.length === 2 && 
                 group.days.includes('Saturday') && 
                 group.days.includes('Sunday')) {
        return `Sat-Sun ${group.hours}`;
      } else {
        // Check for consecutive days
        const dayIndices = group.days.map(day => days.indexOf(day)).sort((a, b) => a - b);
        let consecutive = true;
        for (let i = 1; i < dayIndices.length; i++) {
          if (dayIndices[i] !== dayIndices[i-1] + 1) {
            consecutive = false;
            break;
          }
        }
        
        if (consecutive && dayIndices.length > 1) {
          return `${dayAbbr(days[dayIndices[0]])}-${dayAbbr(days[dayIndices[dayIndices.length - 1]])} ${group.hours}`;
        } else {
          return group.days.map(dayAbbr).join(',') + ' ' + group.hours;
        }
      }
    }).join(', ');
  };

  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-[#374151] mb-2">
        <Clock size={16} className="text-[#374151]" />
        Opening Times
      </label>
      <div className="space-y-3 p-4 border border-gray-300 rounded-lg">
        {days.map(day => (
          <div key={day} className="flex items-center gap-4">
            <div className="w-20 text-sm font-medium text-gray-700">
              {day.substring(0, 3)}
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!schedule[day].closed}
                onChange={(e) => updateSchedule(day, 'closed', !e.target.checked)}
                className="rounded border-gray-300 text-[#374151] focus:ring-[#374151]"
              />
              <span className="text-sm text-gray-600">Open</span>
            </label>
            {!schedule[day].closed && (
              <>
                <input
                  type="time"
                  value={schedule[day].open}
                  onChange={(e) => updateSchedule(day, 'open', e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#374151]"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="time"
                  value={schedule[day].close}
                  onChange={(e) => updateSchedule(day, 'close', e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#374151]"
                />
              </>
            )}
          </div>
        ))}
        
        <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
          <strong>Preview:</strong> {formatSchedule(schedule)}
        </div>
      </div>
    </div>
  );
};

export default OpeningTimesSelector;
